import { defineComponent as _defineComponent } from 'vue'
import LayoutGrid from '@/views/dashboard/LayoutGrid.vue';
import WidgetContainer from '../Charts/widgetContainer/WidgetContainer.vue';
import WidgetDefinition from '@/models/Charts/widgetDefinition';
import {
  availableDashboardLayouts,
  DashboardLayout,
} from '@/models/Charts/dashboardDefinition';
import { WidgetEnum } from '@/models/enums/WidgetEnum';
import { indexedWidgetDefinitions } from '@/utils/misc';
import { FilterTimeSpanEnum } from '@/models/enums/FilterTimeSpanEnum';
import { FilterTimeAxisSpanEnum } from '@/models/enums/FilterTimeAxisSpanEnum';
import { dateRangeToDateStrings } from '@/utils/dates';
import NewFeatureBadge from '../NewFeatureBadge.vue';
import { CutLayoutTab } from './CutLayoutTab';
import { BetaFeature } from '@/models/enums/BetaFeature';
import { currentCustomer, currentUser } from '@/utils/permissionUtils';
import { Aggregate, arrayToNumber } from '@/models/metrics/aggregates';
import { useI18n } from '@/composables/i18n';
import { computed, ref, watch } from 'vue';
import { WidgetEntry } from '@/models/Charts/compositeWidgetDefinition';


/**
 * Tabs containing widgets for cutting devices.
 *
 * To be reused in Work center console > Cut and in a device's page in the
 * eService center.
 */
const __default__ = {};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  __name: 'CutLayout',
  props: {
    activeTab: null,
    selectedDevices: null,
    dateRange: null,
    selectedTimespan: null,
    selectedShifts: null,
    selectedTimeAxisSpan: null
  },
  emits: ["change:tab"],
  setup(__props: any, { emit }: { emit: ((e: 'change:tab', value: CutLayoutTab) => void), expose: any, slots: any, attrs: any }) {

const props = __props as {
  /**
   *  Sets the currently active tab.
   * */
  activeTab: CutLayoutTab;
  /**
   * Devices from which the widgets will show information.
   * */
  selectedDevices: string[];
  /**
   * Start and end dates (inclusive) defining a period for which the widgets
   * will show information.
   *
   * Only used on the Trends tab.
   */
  dateRange: [Date, Date];

  /**
   * Periods of time (day, week...) for which the widgets will show
   * previous/current information.
   *
   * For example, for Week time span it will show data from the last week (up to
   * today) and from the previous one.
   *
   * Only used on the Overview tab.
   */
  selectedTimespan: FilterTimeSpanEnum;
  /**
   *  Work shifts for which the widgets will show information.
   * */
  selectedShifts: number[];
  /**
   * Date grouping that will be applied to the data.
   *
   * Only used on the Trends tab.
   */
  selectedTimeAxisSpan: FilterTimeAxisSpanEnum;
};





const { t } = useI18n();

const activeTabInternal = ref<CutLayoutTab>(CutLayoutTab.CutOverview);
const tableViewLayout = availableDashboardLayouts[DashboardLayout.OneByOne];
const gridLayout = availableDashboardLayouts[DashboardLayout.TwoByThree];

// HACK: This shouldn't be needed but, without it, when we navigate
// (not reload) to the Cut page, the widgets aren't rendered. It looks like
// the dashboard-card elements aren't recreated after updating
// widgetDefinition.
const areWidgetDefinitionsReady = ref(false);

const widgetDefinitions = ref<WidgetDefinition[]>([]);

function onTabChanged(tab: CutLayoutTab) {
  emit('change:tab', tab);
  widgetDefinitions.value = createWidgetDefinitions();
}

function changeTabToOverview() {
  activeTabInternal.value = CutLayoutTab.CutOverview;
  onTabChanged(CutLayoutTab.CutOverview);
}

function createWidgetDefinitions(): WidgetDefinition[] {
  const initialTimeFilter = isTrendsTabActive.value
    ? dateRangeString.value
    : props.selectedTimespan;

  return indexedWidgetDefinitions([
    new WidgetDefinition(
      isTrendsTabActive.value
        ? WidgetEnum.LaserAvailabilityHist
        : WidgetEnum.LaserAvailability,
      props.selectedDevices,
      initialTimeFilter,
      props.selectedShifts,
      undefined,
      props.selectedTimeAxisSpan,
    ),
    isTrendsTabActive.value
      ? new WidgetDefinition(
          WidgetEnum.LaserProductivityHist,
          props.selectedDevices,
          FilterTimeSpanEnum.None,
          props.selectedShifts,
          undefined,
          props.selectedTimeAxisSpan,
          arrayToNumber([Aggregate.Max, Aggregate.Min, Aggregate.Average]),
        )
      : new WidgetDefinition(
          WidgetEnum.CutStatus,
          props.selectedDevices,
          undefined,
          [],
        ),
    new WidgetDefinition(
      isTrendsTabActive.value ? WidgetEnum.LaserOeeHist : WidgetEnum.LaserOee,
      props.selectedDevices,
      initialTimeFilter,
      props.selectedShifts,
      undefined,
      props.selectedTimeAxisSpan,
    ),
    new WidgetDefinition(
      isTrendsTabActive.value
        ? WidgetEnum.LaserStarveBlockHist
        : WidgetEnum.LaserStarveBlock,
      props.selectedDevices,
      initialTimeFilter,
      props.selectedShifts,
      undefined,
      props.selectedTimeAxisSpan,
    ),
    new WidgetDefinition(
      isTrendsTabActive.value
        ? WidgetEnum.LaserOutputAndScrapTimeline
        : WidgetEnum.LaserOutputScrap,
      props.selectedDevices,
      initialTimeFilter,
      props.selectedShifts,
      undefined,
      props.selectedTimeAxisSpan,
    ),
    new WidgetDefinition(
      isTrendsTabActive.value
        ? WidgetEnum.LaserCuttingTimeHist
        : WidgetEnum.LaserCuttingTime,
      props.selectedDevices,
      initialTimeFilter,
      props.selectedShifts,
      undefined,
      props.selectedTimeAxisSpan,
    ),
  ]);
}

function updateWidgetDefinition(
  index: number,
  newWidgetDefinition: WidgetEntry,
) {
  // It's necessary because 'splice' triggers reactivity
  // It appears that Vue is able to track the removal and insertion of the element, not just the direct change in the index.
  widgetDefinitions.value.splice(
    index,
    1,
    newWidgetDefinition as WidgetDefinition,
  );
}

const intradayAvailabilityWidgetDefinition = computed<WidgetDefinition>(
  () =>
    new WidgetDefinition(
      WidgetEnum.LaserIntradayAvailabilityTable,
      props.selectedDevices,
      undefined,
      props.selectedShifts,
      undefined,
      props.selectedTimeAxisSpan,
    ),
);

const cutCurrentWorkloadWidgetDefinition = computed<WidgetDefinition>(
  () =>
    new WidgetDefinition(
      WidgetEnum.LaserCurrentWorkload,
      props.selectedDevices,
    ),
);

const isTrendsTabActive = computed<boolean>(
  () => activeTabInternal.value === CutLayoutTab.CutTrends,
);

const dateRangeString = computed<[string, string]>(() =>
  dateRangeToDateStrings(props.dateRange),
);

const hasCurrentWorkloadBetaFeature = computed(() =>
  currentUser().hasBetaFeature(BetaFeature.WorkCenterConsoleCutCurrentWorkload),
);

const customerHasDataPlatform = computed<boolean>(
  () => currentCustomer()?.hasDataPlatform ?? false,
);

watch(
  () => hasCurrentWorkloadBetaFeature.value,
  (isBetaEnabled: boolean, wasBetaEnabled: boolean) => {
    if (wasBetaEnabled && !isBetaEnabled) {
      if (props.activeTab === CutLayoutTab.CutCurrentWorkload) {
        changeTabToOverview();
      }
    }
  },
);

watch(
  () => props.activeTab,
  () => {
    activeTabInternal.value = props.activeTab;
    if (
      props.activeTab === CutLayoutTab.CutCurrentWorkload &&
      !hasCurrentWorkloadBetaFeature.value
    ) {
      activeTabInternal.value = CutLayoutTab.CutOverview;
    }
    widgetDefinitions.value = createWidgetDefinitions();
    areWidgetDefinitionsReady.value = true;
  },
  { immediate: true },
);

watch(
  () => [
    props.selectedDevices,
    dateRangeString.value,
    props.selectedTimespan,
    props.selectedShifts,
    props.selectedTimeAxisSpan,
  ],
  () => {
    widgetDefinitions.value = widgetDefinitions.value.map((widgetDefinition) =>
      widgetDefinition.getCopy({
        deviceId: props.selectedDevices,
        timeFilter: isTrendsTabActive.value
          ? dateRangeString.value
          : props.selectedTimespan,
        shifts: props.selectedShifts,
        axisSpan: props.selectedTimeAxisSpan,
      }),
    );
  },
);

return { __sfc: true,props, emit, t, activeTabInternal, tableViewLayout, gridLayout, areWidgetDefinitionsReady, widgetDefinitions, onTabChanged, changeTabToOverview, createWidgetDefinitions, updateWidgetDefinition, intradayAvailabilityWidgetDefinition, cutCurrentWorkloadWidgetDefinition, isTrendsTabActive, dateRangeString, hasCurrentWorkloadBetaFeature, customerHasDataPlatform, LayoutGrid, WidgetContainer, NewFeatureBadge, CutLayoutTab }
}

})